import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Upload, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { uploadFile, fileUrl } from "../../lib/storage";

const STATUSES = ["Vision", "Concept", "Pre-Development", "Active", "Topped Out", "Delivered"];

const EMPTY = {
  id: "", name: "", tagline: "", location: "", status: "Vision", type: "",
  year: "", description: "", features: [], hero_image_url: "",
  metrics: {}, featured: true, order: 99,
};

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/projects");
      setProjects(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (p) => setEditing({ ...EMPTY, ...p, features: p.features || [], metrics: p.metrics || {} });
  const startNew = () => setEditing({ ...EMPTY });
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const existing = projects.find((p) => p.id === editing.id);
      const payload = {
        ...editing,
        features: editing.features.filter(Boolean),
        order: Number(editing.order) || 99,
        metrics: editing.metrics || {},
      };
      if (existing) {
        await api.put(`/admin/projects/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/admin/projects", payload);
        toast.success("Project created");
      }
      await load();
      close();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete project "${id}"?`)) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Project deleted");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const handleImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file, "image");
      setEditing((cur) => ({ ...cur, hero_image_url: res.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setUploading(false); }
  };

  return (
    <AdminLayout title="Developments">
      <Toaster theme="dark" position="bottom-right" />
      <div className="flex justify-end mb-6">
        <button data-testid="admin-projects-new" onClick={startNew} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors">
          <Plus className="w-4 h-4" /> New Project
        </button>
      </div>

      <div className="glass overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-projects-table">
          <thead className="border-b border-primary/20">
            <tr className="text-left">
              <th className="overline px-6 py-4">Image</th>
              <th className="overline px-6 py-4">ID</th>
              <th className="overline px-6 py-4">Name</th>
              <th className="overline px-6 py-4">Location</th>
              <th className="overline px-6 py-4">Status</th>
              <th className="overline px-6 py-4">Order</th>
              <th className="overline px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">No projects yet.</td></tr>
            )}
            {projects.map((p) => (
              <tr key={p.id} data-testid={`admin-project-row-${p.id}`} className="border-b border-primary/5 hover:bg-background/30">
                <td className="px-6 py-3">
                  {p.hero_image_url ? (
                    <img src={fileUrl(p.hero_image_url)} alt="" className="w-16 h-12 object-cover" />
                  ) : <div className="w-16 h-12 bg-input" />}
                </td>
                <td className="px-6 py-3 font-mono text-xs text-foreground/60">{p.id}</td>
                <td className="px-6 py-3 font-serif text-lg">{p.name}</td>
                <td className="px-6 py-3 text-foreground/70">{p.location}</td>
                <td className="px-6 py-3 text-foreground/70">{p.status}</td>
                <td className="px-6 py-3 font-mono text-xs">{p.order}</td>
                <td className="px-6 py-3 text-right">
                  <button data-testid={`admin-project-edit-${p.id}`} onClick={() => startEdit(p)} className="p-2 hover:text-primary mr-1"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`admin-project-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-md overflow-y-auto p-6 lg:p-12">
          <div className="max-w-3xl mx-auto glass-strong p-8 lg:p-10">
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="overline mb-2">— {projects.find((p) => p.id === editing.id) ? "Edit" : "Create"}</div>
                <h2 className="font-serif text-3xl">{editing.name || "New project"}</h2>
              </div>
              <button onClick={close} data-testid="admin-projects-close" className="p-2"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={save} data-testid="admin-projects-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="overline mb-2 block">Slug / ID</label>
                <input data-testid="admin-projects-input-id" required disabled={!!projects.find((p) => p.id === editing.id)} value={editing.id} onChange={(e) => setEditing({ ...editing, id: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="w-full bg-background border border-input px-4 py-3 disabled:opacity-50" />
              </div>
              <div>
                <label className="overline mb-2 block">Name</label>
                <input data-testid="admin-projects-input-name" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div className="md:col-span-2">
                <label className="overline mb-2 block">Tagline</label>
                <input value={editing.tagline} onChange={(e) => setEditing({ ...editing, tagline: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div>
                <label className="overline mb-2 block">Location</label>
                <input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div>
                <label className="overline mb-2 block">Type</label>
                <input value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div>
                <label className="overline mb-2 block">Status</label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full bg-background border border-input px-4 py-3">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-2 block">Year / Timeline</label>
                <input value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div className="md:col-span-2">
                <label className="overline mb-2 block">Description</label>
                <textarea rows={5} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full bg-background border border-input px-4 py-3 resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="overline mb-2 block">Features (one per line)</label>
                <textarea rows={5} value={(editing.features || []).join("\n")} onChange={(e) => setEditing({ ...editing, features: e.target.value.split("\n") })} className="w-full bg-background border border-input px-4 py-3 resize-none font-mono text-xs" />
              </div>
              <div className="md:col-span-2">
                <label className="overline mb-2 block">Hero image</label>
                <div className="flex items-center gap-4">
                  {editing.hero_image_url && (
                    <img src={fileUrl(editing.hero_image_url)} alt="" className="w-32 h-20 object-cover border border-input" />
                  )}
                  <label className="inline-flex items-center gap-2 border border-primary/40 text-primary px-4 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading…" : "Upload Image"}
                    <input data-testid="admin-projects-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} />
                  </label>
                  <input placeholder="…or paste an image URL" value={editing.hero_image_url || ""} onChange={(e) => setEditing({ ...editing, hero_image_url: e.target.value })} className="flex-1 bg-background border border-input px-4 py-3 font-mono text-xs" />
                </div>
              </div>
              <div>
                <label className="overline mb-2 block">Display order</label>
                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-full bg-background border border-input px-4 py-3" />
              </div>
              <div className="flex items-end gap-3">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                  <span className="text-sm">Featured</span>
                </label>
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-primary/10">
                <button type="button" onClick={close} className="px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-foreground/60 hover:text-foreground">Cancel</button>
                <button data-testid="admin-projects-save" type="submit" disabled={busy} className="bg-primary text-primary-foreground px-6 py-3 text-[12px] uppercase tracking-[0.22em] disabled:opacity-60">
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
